import { globalHistory, navigate } from '@reach/router';
import sortedIndexBy from 'lodash/sortedIndexBy';
import sortedLastIndexBy from 'lodash/sortedLastIndexBy';
import { DateTime, DateTimeUnit, Duration, Interval } from 'luxon';
import queryString from 'query-string';

import { AdminAPIResponse, CustomerAPIResponse, ErrorResponse } from '../interfaces/backend-api';
import { API_DATE_FORMAT, Equipment, MaintenancePlan, Site } from '../interfaces/customer-api';
import {
    CustomerUser,
    DateRange,
    InfiniteDateRange,
    InfiniteISOTimeRange,
    InternalUser,
    ISOTimeRange,
    Language,
    LoadingState,
    PublicEnv,
    UserLanguage,
} from '../interfaces/internal';
import { Customer } from '../interfaces/admin-api';

const LOCAL_STORAGE_NEXT_URL_KEY = 'mykalmar-next-url';
const SESSION_STORAGE_ADMIN_STATUS_KEY = 'mykalmar-admin-status';
const SESSION_STORAGE_ADMIN_SELECTED_CUSTOMER_KEY = 'mykalmar-selected-customer-number';

export const IS_PRODUCTION_ENV = location.host === 'www.mykalmar.com';

// TODO: configure logging properly
export const logger = console;

export const mkEnv: PublicEnv = { ...window.__mkEnv };

export class APIError extends Error {
    constructor(message: string, public statusCode: number = 500) {
        super(message);
        Object.setPrototypeOf(this, APIError.prototype);
    }
}

export function removeHash(): void {
    const location = window.location;
    if ('replaceState' in history) {
        history.replaceState('', document.title, location.pathname + location.search);
    } else {
        // Prevent scrolling by storing the page's current scroll offset
        const scrollV = document.body.scrollTop;
        const scrollH = document.body.scrollLeft;

        location.hash = '';

        // Restore the scroll offset, should be flicker free
        document.body.scrollTop = scrollV;
        document.body.scrollLeft = scrollH;
    }
}

type MaybeClassName = string | boolean | null | undefined;

export function cns(...classNames: MaybeClassName[]): string {
    const strings = classNames.filter((className) => typeof className === 'string');
    return strings.join(' ');
}

async function expireSession() {
    // Dynamic import to avoid circular dependencies
    const { store } = await import('./state');
    store.actions.expireSession();
}

async function handleAPIResponse<T>(response: Response): Promise<T> {
    if (response.status === 401) {
        // Session expired, show dialog
        await expireSession();
        throw new APIError(response.statusText, 401);
    } else if (response.status >= 400) {
        const result = (await response.json()) as ErrorResponse;
        const message = result.errors && result.errors.length > 0 ? result.errors[0].title : 'API error';
        throw new APIError(message, response.status);
    }

    return response.json() as Promise<T>;
}

export async function queryAPI<T>(endpoint: string): Promise<T> {
    const url = `${mkEnv.apiBaseURL}/api/v1${endpoint}`;
    const response = await fetch(url, {
        credentials: 'include',
    });
    return handleAPIResponse(response);
}

export async function postToAPI<TBody, TResult>(endpoint: string, body: TBody): Promise<TResult> {
    const url = `${mkEnv.apiBaseURL}/api/v1${endpoint}`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'include',
    });
    return handleAPIResponse(response);
}

export async function putToAPI<TBody, TResult>(endpoint: string, body: TBody): Promise<TResult> {
    const url = `${mkEnv.apiBaseURL}/api/v1${endpoint}`;
    const response = await fetch(url, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'include',
    });
    return handleAPIResponse(response);
}

export async function deleteToAPI<TBody, TResult>(endpoint: string, body: TBody): Promise<TResult> {
    const url = `${mkEnv.apiBaseURL}/api/v1${endpoint}`;
    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'include',
    });
    return handleAPIResponse(response);
}

export async function queryCustomerAPI<T>(endpoint: string): Promise<CustomerAPIResponse<T>> {
    return queryAPI<CustomerAPIResponse<T>>(endpoint);
}

export async function queryAdminAPI<T>(endpoint: string): Promise<AdminAPIResponse<T>> {
    return queryAPI<AdminAPIResponse<T>>(endpoint);
}

export const parseUser = (user: string): InternalUser | CustomerUser => {
    const parsedUser = JSON.parse(user);
    if (parsedUser.isAdmin) {
        return parsedUser as InternalUser;
    }
    return parsedUser as CustomerUser;
};

export const getLanguageCode = (language: UserLanguage): Language | undefined => Language[language];

export const setNextURL = (): void => {
    const excludedURLs = ['/login', '/internal'];
    const { next } = queryString.parse(window.location.search);
    if (typeof next === 'string') {
        const isExcluded = excludedURLs.some((url) => url === next);
        if (!isExcluded && localStorage) {
            localStorage.setItem(LOCAL_STORAGE_NEXT_URL_KEY, decodeURIComponent(next));
        }
    }
};

export const navigateToNextURL = (): void => {
    if (localStorage) {
        const nextURL = localStorage.getItem(LOCAL_STORAGE_NEXT_URL_KEY);
        if (nextURL) {
            navigate(nextURL);
            clearNextURL();
        }
    }
};

export const clearNextURL = (): void => {
    if (localStorage) {
        localStorage.removeItem(LOCAL_STORAGE_NEXT_URL_KEY);
    }
};

export const saveAdminStatus = (isAdmin: boolean): void => {
    if (sessionStorage) {
        sessionStorage.setItem(SESSION_STORAGE_ADMIN_STATUS_KEY, String(isAdmin ? 1 : 0));
    }
};

export const getLoginURL = (): string => {
    if (sessionStorage) {
        const adminStatus = sessionStorage.getItem(SESSION_STORAGE_ADMIN_STATUS_KEY);
        if (adminStatus) {
            const isAdmin = parseInt(adminStatus, 10);
            return isAdmin ? '/internal' : '/login';
        }
    }
    return '/login';
};

export const saveCustomerNumber = (customerNumber: string): void => {
    if (sessionStorage) {
        sessionStorage.setItem(SESSION_STORAGE_ADMIN_SELECTED_CUSTOMER_KEY, customerNumber);
    }
};

export const getSavedCustomerNumber = (): string | null => {
    if (sessionStorage) {
        const customerNumber = sessionStorage.getItem(SESSION_STORAGE_ADMIN_SELECTED_CUSTOMER_KEY);
        if (customerNumber) {
            return customerNumber;
        }
    }
    return null;
};

const isLoadingState = (targetLoadingState: LoadingState, loadingStates?: LoadingState | LoadingState[]): boolean => {
    const _loadingStates = Array.isArray(loadingStates) ? loadingStates : [loadingStates];

    return _loadingStates.some((loadingState) => loadingState === targetLoadingState);
};

export const isLoading = (loadingStates?: LoadingState | LoadingState[]): boolean =>
    isLoadingState(LoadingState.Loading, loadingStates);

export const isFailed = (loadingStates?: LoadingState | LoadingState[]): boolean =>
    isLoadingState(LoadingState.Failed, loadingStates);

export const isSuccessful = (loadingStates?: LoadingState | LoadingState[]): boolean =>
    isLoadingState(LoadingState.Successful, loadingStates);

export function formatHoursAndMinutes(minutes: number): string {
    const duration = Duration.fromObject({ minutes });
    const isEvenHour = minutes > 0 && minutes % 60 === 0;
    if (isEvenHour) {
        return duration.toFormat("h'h'");
    } else if (minutes < 60) {
        return duration.toFormat("m'min'");
    }

    return duration.toFormat("h'h' m'min'");
}

export function formatDate(date: DateTime): string {
    if (!date.isValid) {
        return '-';
    }
    return date
        .setLocale(navigator.language)
        .toLocaleString({ ...DateTime.DATE_SHORT, day: '2-digit', month: '2-digit' });
}

export function formatEstimatedAvailability(
    estimatedAvailability: unknown,
    readyText: string,
    fallback = '',
): string {
    if (!estimatedAvailability) return fallback;
    const targetDate = DateTime.fromISO(String(estimatedAvailability).substring(0, 10));
    if (!targetDate.isValid) return fallback;
    if (targetDate < DateTime.now().startOf('day')) return readyText;
    return formatDate(targetDate);
}

export function formatTime(date: DateTime): string {
    if (!date.isValid) {
        return '-';
    }
    return date.setLocale(navigator.language).toLocaleString({ ...DateTime.TIME_24_SIMPLE });
}

export function dateRangeToTimeRange(dateRange: DateRange): ISOTimeRange {
    return {
        startTime: DateTime.fromFormat(dateRange.startDate, API_DATE_FORMAT).startOf('day').toISO(),
        endTime: DateTime.fromFormat(dateRange.endDate, API_DATE_FORMAT).endOf('day').toISO(),
    };
}

export function possiblyInfiniteDateRangeToTimeRange(dateRange: InfiniteDateRange): InfiniteISOTimeRange {
    return {
        startTime: DateTime.fromFormat(dateRange.startDate, API_DATE_FORMAT).startOf('day').toISO(),
        endTime: dateRange.endDate
            ? DateTime.fromFormat(dateRange.endDate, API_DATE_FORMAT).endOf('day').toISO()
            : undefined,
    };
}

export function getIntervals(start: DateTime, end: DateTime, splitBy: DateTimeUnit): Interval[] {
    return Interval.fromDateTimes(start.startOf(splitBy), end.endOf(splitBy)).splitBy(
        Duration.fromObject({ [splitBy]: 1 }),
    );
}

/**
 * Can be thrown to display errors that are intended to be seen by the user
 */
export class UserError extends Error {
    constructor(message: string) {
        super(message);
        Object.setPrototypeOf(this, UserError.prototype);
    }
}
export const getMaintenancePlansForDateRange = (
    maintenancePlans: MaintenancePlan[],
    dateRange: DateRange,
): MaintenancePlan[] => {
    if (maintenancePlans.length === 0) return [];

    const indexByKey: keyof MaintenancePlan = 'date';

    // Maintenance plans are sorted, so we can use binary search
    const firstIndexOfDate = sortedIndexBy(
        maintenancePlans as Partial<MaintenancePlan>[],
        { date: dateRange.startDate },
        indexByKey,
    );
    const lastIndexOfDate = sortedLastIndexBy(
        maintenancePlans as Partial<MaintenancePlan>[],
        { date: dateRange.endDate },
        indexByKey,
    );

    return maintenancePlans.slice(firstIndexOfDate, lastIndexOfDate);
};

/**
 * Anonymizes a string or number by hashing it to a hexadecimal string.
 * @param value to be anonymized
 * @returns anonymized value as a hexadecimal string or the original value as a string if an error occurs during anonymization
 */
export const anonymize = (value: string | number): string => {
    try {
        const str = String(value);
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const chr = str.charCodeAt(i);
            hash = (hash << 5) - hash + chr;
            hash |= 0; // Convert to 32‑bit integer
        }
        return Math.abs(hash).toString(16);
    } catch (error) {
        return value.toString();
    }
};

const initOptanonConsentNoticeStart = () => {
    const cookieLawScript = document.createElement('script');
    cookieLawScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    cookieLawScript.type = 'text/javascript';
    // cookieLawScript.charset="UTF-8"
    cookieLawScript.setAttribute('data-domain-script', 'e3a58f77-9bfd-4aa5-ab4a-3141b294219e');
    cookieLawScript.setAttribute('data-document-language', 'true');
    document.head.appendChild(cookieLawScript);

    const optanonWrapperScript = document.createElement('script');
    optanonWrapperScript.type = 'text/javascript';
    optanonWrapperScript.innerHTML = `function OptanonWrapper() { }`;
    document.head.appendChild(optanonWrapperScript);
};

const initGoogleTagManagerNoscript = (gtmId: string) => {
    const iframElement = document.createElement('iframe');
    iframElement.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframElement.height = '0';
    iframElement.width = '0';
    iframElement.style.display = 'none';
    iframElement.style.visibility = 'hidden';

    const nonScriptElement = document.createElement('noscript');
    nonScriptElement.appendChild(iframElement);
    document.getElementsByTagName('body')[0].insertAdjacentElement('afterend', nonScriptElement);
};

const initDefaultConsentStatus = (): void => {
    const gtagDataLayerScript = document.createElement('script');
    gtagDataLayerScript.innerHTML = `
        function gtag() {
            window.dataLayer.push(arguments);
        }
        window.dataLayer = window.dataLayer || [];
        gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            functionality_storage: 'denied',
            personalization_storage: 'denied',
            security_storage: 'granted',
            wait_for_update: 500,
        });
        gtag('set', 'ads_data_redaction', !0);
    `;
    document.head.appendChild(gtagDataLayerScript);
};

const initGoogleTagManager = (gtmId: string): void => {
    const existingElement = document.getElementById('GTM');
    if (existingElement !== null) {
        logger.log('GTM already initialized');
        return;
    }

    window.dataLayer.push({ event: 'gtm.js', 'gtm.start': new Date().getTime() });

    const scriptElement = document.createElement('script');
    scriptElement.id = 'GTM';
    scriptElement.async = true;
    scriptElement.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(scriptElement);
};

const initGTMUserData = (user: CustomerUser | InternalUser): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'initData',
        originalLocation: document.location.href,
        pageData: {
            pagePath: document.location.pathname + document.location.search,
        },
        userData: {
            companyId: user.isAdmin ? 'internal' : user.sapCustomerNumber || 'unknown',
            userId: anonymize(user.id),
            role: user.role,
        },
    });
    globalHistory.listen(({ location }) => {
        window.dataLayer.push({
            event: 'pageEvent',
            pageData: { pagePath: location.pathname + location.search },
        });
    });
};

export const initCookieConsent = (gtmId: string, user: CustomerUser | InternalUser): void => {
    //anything goes wrong in cookies initialization, we dont want to break the login flow
    try {
        if (!(window.__isTestEnvironment ?? false)) {
            initDefaultConsentStatus();
            initOptanonConsentNoticeStart();
            initGoogleTagManager(gtmId);
            initGoogleTagManagerNoscript(gtmId);
            initGTMUserData(user);
        }
    } catch (error) {
        logger.error('Failed to initialize cookies');
    }
};

export function getCaseManagementLink(path: string): string {
    // add session=true param so SF knows not to redirect back to MyKalmar
    return `${mkEnv.salesforceCommunityURL}${path}?session=true`;
}

export function nonNullable<T>(value: T): value is NonNullable<T> {
    return value !== null && value !== undefined;
}

export function getInsightLink(path?: string): string {
    const baseURL = mkEnv.insightURL;
    return [baseURL, path].filter(nonNullable).join('/');
}

export function getTechsightLink(path?: string): string {
    const baseURL = mkEnv.techsightUrl;
    return [baseURL, path].filter(nonNullable).join('/');
}

export function isCustomerUser(user: InternalUser | CustomerUser | null): user is CustomerUser {
    return user !== null && !user.isAdmin;
}

export function getShortEquipmentType(equipmentType: string): string {
    // If type is one word, dont shorten
    if (!/\s/.test(equipmentType)) {
        return equipmentType;
    }

    // Return the first character of every word in uppercase
    const matches = equipmentType.match(/\b(\w)/g);
    if (matches) {
        return matches.join('').toUpperCase();
    }

    // Fallback:
    return equipmentType;
}

export const getHostType = () => {
    const host = location.host;
    if (host === 'www.mykalmar.com') return 'production';
    if (host === 'mykalmar-pilot.mykalmar.ncic.cargotec.com') return 'pilot';
    if (host === 'mykalmar-dev.mykalmar.ncic.cargotec.com') return 'dev';
    if (host.startsWith('localhost') || host.startsWith('127.0.0.1')) return 'local_development';

    return 'other';
};

const getCountryCodeFromSites = (sites: Site[], selectedSite: string | null, selectedCustomer: Customer | null) => {
    if (selectedSite !== null) {
        return sites.find((site) => site.ship_to_number === selectedSite)?.country_code;
    } else {
        return sites.find((site) => selectedCustomer?.sold_to_number === site.ship_to_number)?.country_code;
    }
};

export const getMascusLink = (selectedSite: string | null, sites: Site[], selectedCustomer: Customer | null) => {
    const countryCode =
        sites.length > 0
            ? getCountryCodeFromSites(sites, selectedSite, selectedCustomer)
            : selectedCustomer?.country?.toLowerCase() === 'germany'
            ? 'DE'
            : '';

    return countryCode === 'DE' ? 'https://gebrauchtmaschinen.kalmar.de/' : 'https://used.kalmarglobal.com/';
};
export const getDealerCommunityLink = () => {
    return 'https://dealer.mykalmar.com/';
};

export const getInsightRegisterLink = () => {
    return 'https://www.kalmarglobal.com/parts-services/kalmar-insight/';
};

export const getStoreRegisterLink = () => {
    return 'https://www.kalmarglobal.com/parts-services/kalmar-parts-myparts-webshop/';
};

export const getKalmarMaintenanceLink = () => {
    return 'https://www.kalmarglobal.com/news--insights/articles/2020/20200903_maintenance-matters/';
};

export const getKalmarCareLink = () => {
    return 'https://www.kalmarglobal.com/parts-services/kalmar-care-maintenance-contract/';
};

export function escapeHtml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/\//g, '&#x2F;');
}

export const mapShipTos = (sites: Site[]) => {
    return sites.map((site) => ({
        id: site.street_address,
        label: site.street_address,
    }));
};

export const mapSerialNumbers = (equipment: Equipment[]) => {
    return equipment.map((eq) => ({
        id: eq.serial_number,
        label: eq.serial_number,
    }));
};

export const changeOneTrustLanguage = (languageCode: Language) => {
    if (window.OneTrust?.changeLanguage) {
        window.OneTrust.changeLanguage(languageCode);
    } else {
        // If OneTrust hasn't loaded yet, chain into OptanonWrapper so it runs once OneTrust initializes
        const previousOptanonWrapper = window.OptanonWrapper;
        window.OptanonWrapper = () => {
            previousOptanonWrapper?.();
            window.OneTrust?.changeLanguage(languageCode);
        };
    }
};

export const toTitleCase = (value: string) => {
    if (!value) {
        return value;
    }
    if (value.length === 1) {
        return value.toLocaleUpperCase();
    }
    return value.charAt(0).toLocaleUpperCase() + value.slice(1).toLocaleLowerCase();
};
